import { getCompanyCodeFromToken, getUserFromToken } from "./authService";
import { API_URL } from "./apiConfig";

const companyCode = getCompanyCodeFromToken();

export const checkUserExists = async (userName, password, companyCode) => {
  try {
    const response = await fetch(`${API_URL}User/UserExists/${userName}/${password}/${companyCode}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    if (response.ok) return await response.json();
    throw new Error('Invalid credentials or server error');
  } catch (error) {
    console.error("API call failed:", error);
    throw error;
  }
};

export const getUserHierarchy = async () => {
  try {
    const response = await fetch(`${API_URL}User/GetUserHierarchy/${companyCode}`);
    if (!response.ok) throw new Error("Failed to fetch user hierarchy");
    return await response.json();
  } catch (error) {
    console.error("Error fetching user hierarchy:", error);
    throw error;
  }
};

export const CheckUserExistsMultiple = async (userName, companyCode, departmentId) => {
  try {
    const response = await fetch(`${API_URL}User/CheckUserExistsMultiple/${userName}/${companyCode}/${departmentId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    if (response.ok) return await response.json();
    throw new Error('Invalid credentials or server error');
  } catch (error) {
    console.error("API call failed:", error);
    throw error;
  }
};

export const registerUser = async (userData) => {
  try {
    const response = await fetch(`${API_URL}User/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData),
    });
    if (response.ok) return await response.json();
    return { status: response.status };
  } catch (error) {
    console.error("API call failed:", error);
    throw error;
  }
};

export const changePassword = async (username, newPassword) => {
  try {
    const url = `${API_URL}User/change-password/${encodeURIComponent(username)}/${encodeURIComponent(newPassword)}`;
    const response = await fetch(url, { method: 'POST' });
    if (response.ok) {
      const message = await response.text();
      return { success: true, message };
    }
    const errorText = await response.text();
    return { success: false, status: response.status, message: errorText };
  } catch (error) {
    console.error("Change password failed:", error);
    throw error;
  }
};

export const fetchRolePermissions = async (role) => {
  try {
    const response = await fetch(`${API_URL}User/GetPermissionsForRole/${role}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) throw new Error('Failed to fetch role permissions');
    return await response.json();
  } catch (error) {
    console.error("Error fetching role permissions:", error);
    throw error;
  }
};

export const fetchUsersByRole = async (role) => {
  try {
    const response = await fetch(`${API_URL}User/GetUsersByRole/${companyCode}/${encodeURIComponent(role)}`);
    if (!response.ok) throw new Error("Failed to fetch users by role");
    return await response.json();
  } catch (error) {
    console.error("Error fetching users by role:", error);
    return [];
  }
};

export const fetchHiringManagers = async () => {
  try {
    const response = await fetch(`${API_URL}User/GetAllusers/${companyCode}`);
    if (!response.ok) throw new Error("Failed to fetch users");
    return await response.json();
  } catch (error) {
    console.error("Error fetching hiring managers:", error);
    return [];
  }
};

export const GetAllUsers = async () => {
  try {
    const response = await fetch(`${API_URL}User/GetUsersAsync/${companyCode}`);
    if (!response.ok) throw new Error("Failed to fetch users");
    return await response.json();
  } catch (error) {
    console.error("Error fetching users:", error);
    return [];
  }
};

export const UpdateUsers = async (userData) => {
  try {
    userData.companyCode = getCompanyCodeFromToken();
    const response = await fetch(`${API_URL}User/UpdateUser`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData),
    });
    if (response.ok) return await response.json();
    return { status: response.status };
  } catch (error) {
    console.error("API call failed:", error);
    throw error;
  }
};

export const fetchPermissions = async () => {
  try {
    const response = await fetch(`${API_URL}User/fetchPermissions`);
    if (!response.ok) throw new Error("Failed to fetch permissions");
    return await response.json();
  } catch (error) {
    console.error("Error fetching permissions:", error);
    return [];
  }
};

export const AddRolePermission = async (newRequest) => {
  try {
    const response = await fetch(`${API_URL}User/AddRolePermission`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newRequest),
    });
    if (response.ok) return await response.json();
    return { status: response.status };
  } catch (error) {
    console.error("API call failed:", error);
    throw error;
  }
};

export const updateRolePermission = async (newRequest) => {
  try {
    const response = await fetch(`${API_URL}User/updateRolePermission`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newRequest),
    });
    if (response.ok) return await response.json();
    return { status: response.status };
  } catch (error) {
    console.error("API call failed:", error);
    throw error;
  }
};

export const deleteRolePermission = async (id) => {
  try {
    const response = await fetch(`${API_URL}User/DeleteRolePermission/${id}`, {
      method: 'DELETE',
    });
    if (response.ok) return await response.json();
    return { status: response.status };
  } catch (error) {
    console.error("API call failed:", error);
    throw error;
  }
};

export const CheckSSOUser = async (userName) => {
  try {
    const response = await fetch(`${API_URL}User/CheckSSOUser/${userName}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    if (response.ok) return await response.json();
    throw new Error('Invalid credentials or server error');
  } catch (error) {
    console.error("API call failed:", error);
    throw error;
  }
};

export const GetAllEmailTemplates = async (templateName) => {
  try {
    const response = await fetch(`${API_URL}User/GetAllEmailTemplates/${templateName}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) throw new Error('Failed to fetch email templates');
    return await response.json();
  } catch (error) {
    console.error("Error fetching email templates:", error);
    throw error;
  }
};

export const GetAllEmailTemplatesBylevel = async (templateName, level, compCode) => {
  try {
    const response = await fetch(`${API_URL}User/GetAllEmailTemplatesBylevel/${templateName}/${level}/${compCode}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) throw new Error('Failed to fetch email templates');
    return await response.json();
  } catch (error) {
    console.error("Error fetching email templates:", error);
    throw error;
  }
};

export const fetchCompany = async () => {
  try {
    const response = await fetch(`${API_URL}User/GetCompany/${companyCode}`);
    if (!response.ok) throw new Error("Failed to fetch company");
    return await response.json();
  } catch (error) {
    console.error("Error fetching company:", error);
    return [];
  }
};

export const fetchCompanyloged = async () => {
  try {
    const response = await fetch(`${API_URL}User/GetCompany/${null}`);
    if (!response.ok) throw new Error("Failed to fetch company");
    return await response.json();
  } catch (error) {
    console.error("Error fetching company:", error);
    return [];
  }
};

export const GetCompanyByUser = async () => {
  try {
    const response = await fetch(`${API_URL}User/GetCompanyByUser/${getUserFromToken()}`);
    if (!response.ok) throw new Error("Failed to fetch company");
    return await response.json();
  } catch (error) {
    console.error("Error fetching company:", error);
    return [];
  }
};
